import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { LeadProposalGenerationType } from '@econtractor/types';
import { UserNameItem } from 'modules/user';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { withBaseCurrency, withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import GenerationLogEmailCell from '../../share/GenerationLogEmailCell';
import GenerationLogCommentCell from '../../share/GenerationLogCommentCell';
import ShareDocument from 'components/ShareLink';

interface ProposalGenerateLogTableProps {
    rows: LeadProposalGenerationType[];
    togglingId: number | null;
    generating: boolean;
    acceptLocked: boolean;
    onToggleAccept: (generationId: number, currentlyAccepted: boolean) => void | Promise<void>;
    onSendEmail: (generationId: number) => void;
}

const formatGenerationAmount = (amount: number, isUsd: boolean) => {
    const formatted = formatPrice(safeFloatParse(amount || 0));
    return isUsd ? withBaseCurrency(formatted) : withLocalCurrency(formatted);
};

const ProposalGenerateLogTable = ({
    rows,
    togglingId,
    generating,
    acceptLocked,
    onToggleAccept,
    onSendEmail,
}: ProposalGenerateLogTableProps) => {
    const { t } = useTranslation('Proposal');

    return (
        <div className="proposal-generate-log-table-wrap">
            <Table size="sm" className="ProposalGenerateLogTable table-hover mt-2 mb-0 align-middle">
                <thead>
                    <tr className="bg-light">
                        <td className="head-user">{t('edit.generate.logUser')}</td>
                        <td className="head-date">{t('edit.generate.logDate')}</td>
                        <td className="head-email">{t('edit.generate.logEmail')}</td>
                        <td className="head-amount text-end">{t('edit.generate.logAmount')}</td>
                        <td className="head-comment">{t('edit.generate.logComment')}</td>
                        <td className="head-accept text-center">{t('edit.generate.logAccept')}</td>
                        <td className="head-file">{t('edit.generate.logFile')}</td>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const generatedAt = DT.fromMillis(row.generatedCreated || 0);
                        const isAccepted = Boolean(row.proposavAccept);
                        const isToggling = togglingId === row.ID;
                        const fileUrl = String(row.filePublicUrl || '').trim();
                        return (
                            <tr key={row.ID} className={isAccepted ? 'is-accepted' : undefined}>
                                <td className="user-cell">
                                    <UserNameItem
                                        user={{
                                            ID: row.ownerId,
                                            userName: row.userName || `#${row.ownerId}`,
                                            userEmail: row.userEmail || '',
                                            userPicture: row.userPicture || '',
                                        }}
                                    />
                                </td>
                                <td className="date-cell">
                                    <div>{generatedAt.toFormat('dd.MM.yyyy')}</div>
                                    <div className="text-muted">{generatedAt.toFormat('HH:mm')}</div>
                                </td>
                                <td className="email-cell">
                                    <GenerationLogEmailCell
                                        sentToEmail={row.sentToEmail}
                                        sentEmail={row.sentEmail}
                                        notSentLabel={t('edit.generate.logNotSent')}
                                        sentNoEmailLabel={t('edit.generate.logSentNoEmail')}
                                        sendTitle={t('edit.generate.sendDialog.btnTitle')}
                                        disabled={generating}
                                        onSend={() => onSendEmail(row.ID)}
                                    />
                                </td>
                                <td className="amount-cell text-end">
                                    {formatGenerationAmount(row.proposalAmount, Boolean(row.proposalUsd))}
                                </td>
                                <td className="comment-cell">
                                    <GenerationLogCommentCell
                                        generationComment={row.generationComment}
                                        proposalEmailBody={row.proposalEmailBody}
                                        generationTitle={t('edit.generate.logCommentHint')}
                                        emailBodyTitle={t('edit.generate.logEmailBodyHint')}
                                    />
                                </td>
                                <td className="accept-cell text-center">
                                    <button
                                        type="button"
                                        className={`btn btn-sm proposal-accept-btn ${
                                            isAccepted ? 'btn-light-success is-on' : 'btn-light'
                                        }`}
                                        title={
                                            acceptLocked
                                                ? t('edit.generate.acceptLockedTitle')
                                                : isAccepted
                                                  ? t('edit.generate.acceptUnsetTitle')
                                                  : t('edit.generate.acceptSetTitle')
                                        }
                                        aria-label={
                                            acceptLocked
                                                ? t('edit.generate.acceptLockedTitle')
                                                : isAccepted
                                                  ? t('edit.generate.acceptUnsetTitle')
                                                  : t('edit.generate.acceptSetTitle')
                                        }
                                        aria-pressed={isAccepted}
                                        disabled={acceptLocked || isToggling || generating}
                                        onClick={() => void onToggleAccept(row.ID, isAccepted)}
                                    >
                                        {isToggling ? (
                                            <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                        ) : (
                                            <i
                                                className={`bi ${
                                                    isAccepted ? 'bi-check-circle-fill' : 'bi-check-circle'
                                                }`}
                                            ></i>
                                        )}
                                    </button>
                                </td>
                                <td className="file-cell">
                                    {fileUrl ? (
                                        <div className="file-cell-row">
                                            <a href={fileUrl} target="_blank" rel="noreferrer">
                                                {row.fileName || t('edit.generate.logOpenFile')}
                                            </a>
                                            <ShareDocument
                                                text={row.proposalName || ''}
                                                link={fileUrl}
                                                inputMessageDlg
                                                disabled={generating}
                                            />
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

export default ProposalGenerateLogTable;
